import * as React from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, AlertCircle, Network } from 'lucide-react';
import { validateCidr, calculateCidrHosts, CidrValidationResult } from '@/lib/validation/cidr';

// Re-export for convenience
export { validateCidr, type CidrValidationResult } from '@/lib/validation/cidr';

export interface CidrInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
  onValidationChange?: (result: CidrValidationResult) => void;
  showValidation?: boolean;
  /** Show validation UI for valid pre-filled values even before user interaction */
  showInitialValidation?: boolean;
  mode?: 'cidr' | 'ip';
}

const CidrInput = React.forwardRef<HTMLInputElement, CidrInputProps>(
  ({ className, value, onChange, onValidationChange, showValidation = true, showInitialValidation = true, mode = 'cidr', ...props }, ref) => {
    const [hasInteracted, setHasInteracted] = React.useState(false);

    const validation = React.useMemo(() => validateCidr(value, mode), [value, mode]);

    React.useEffect(() => {
      onValidationChange?.(validation);
    }, [validation, onValidationChange]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!hasInteracted) setHasInteracted(true);
      onChange(e.target.value);
    };

    // Show validation UI if:
    // - showValidation is enabled AND value exists AND
    // - Either user has interacted OR (showInitialValidation is true AND value is valid)
    const showValidationState = showValidation && value.length > 0 &&
      (hasInteracted || (showInitialValidation && validation.isValid));
    const isValid = validation.isValid;

    return (
      <div className="space-y-2">
        <div className="relative">
          {/* Network icon */}
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <Network
              className={cn(
                'h-4 w-4 transition-colors duration-200',
                showValidationState
                  ? isValid
                    ? 'text-emerald-500'
                    : 'text-red-400'
                  : 'text-muted-foreground'
              )}
            />
          </div>

          <input
            type="text"
            ref={ref}
            value={value}
            onChange={handleChange}
            placeholder={mode === 'cidr' ? "e.g., 192.168.1.0/24" : "e.g., 192.168.1.5"}
            className={cn(
              'flex h-11 w-full rounded-lg border bg-[#111] pl-10 pr-10 py-2 text-sm font-mono text-white',
              'ring-offset-background transition-all duration-200',
              'placeholder:text-white/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              showValidationState
                ? isValid
                  ? 'border-emerald-500/50 focus-visible:ring-emerald-500/50'
                  : 'border-red-500/50 focus-visible:ring-red-500/50'
                : 'border-[#2E3835] focus-visible:ring-ring',
              className
            )}
            {...props}
          />

          {/* Validation indicator */}
          {showValidationState && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              {isValid ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-500 animate-in fade-in-0 zoom-in-50 duration-200" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-400 animate-in fade-in-0 zoom-in-50 duration-200" />
              )}
            </div>
          )}
        </div>

        {/* Validation message / network info */}
        {showValidationState && (
          <div
            className={cn(
              'flex items-center gap-2 text-xs animate-in slide-in-from-top-1 duration-200',
              isValid ? 'text-emerald-400' : 'text-red-400'
            )}
          >
            {isValid ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2 py-0.5 font-medium">
                  {validation.networkType}
                </span>
                {mode === 'cidr' && (
                  <span className="text-muted-foreground">
                    /{validation.prefixLength} • {calculateCidrHosts(validation.prefixLength!, validation.networkType === 'IPv6')}
                  </span>
                )}
                {mode === 'ip' && (
                  <span className="text-muted-foreground">Valid IP Address</span>
                )}
              </>
            ) : (
              <span>{validation.errorMessage}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

CidrInput.displayName = 'CidrInput';

export { CidrInput };
